import {
  BASE_WELL_LAYER_PARENT,
  FRAC_LAYER,
  GEO_MODEL_LAYERS,
  POLYGON_BINS_LAYERS,
  POLYGON_BINS_MY_FIELDS_LAYERS,
  POLYGON_BINS_ORG_FIELDS_LAYERS,
  POLYGON_BINS_PROJECT_FIELDS_LAYERS,
  RIG_FRAC_LAYER_ROOT,
  RIG_LAYER
} from "constants/mapLayers.constants";

import { ProjectTreeNodeT } from "components/project/layers/hooks";
// Imported from the concrete module, not the hooks barrel: this file computes
// ROOT_LAYER_FOLDER_NAMES at module scope, and pulling runtime values through
// the barrel forms an import cycle that leaves them undefined in some suites.
import {
  GRID_LAYERS_SECTION,
  GRID_LAYER_FOLDERS
} from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";

import { useStructuredNodeContext } from "./context/StructuredNodeContext";
import { RootFolder } from "./nodes/RootFolder";
import { LayerNode } from "./nodes/layer-nodes/LayerNode";
import { McDanielShapefileNode } from "./nodes/mcdaniel-nodes/McDanielShapefileNode";
import { OrganizationShapefileNode } from "./nodes/organization-nodes/OrganizationShapefileNode";
import { PolygonBinFolder } from "./nodes/polygon-bin-nodes/PolygonBinFolder";
import { PolygonBinNode } from "./nodes/polygon-bin-nodes/PolygonBinNode";
import { RigFracNode } from "./nodes/rig-frac-nodes/RigFracNode";
import { EditableShapefileFolderNode } from "./nodes/shapefile-folder-nodes/EditableShapefileFolderNode";
import { ShapefileFolderNode } from "./nodes/shapefile-folder-nodes/ShapefileFolderNode";
import { EditableShapefileNode } from "./nodes/shapefile-nodes/EditableShapefileNode";
import { RootShapefileFolder } from "./nodes/shapefile-nodes/RootShapefileFolder";
import { ShapefileNode } from "./nodes/shapefile-nodes/ShapefileNode";

export interface IStructuredNode {
  node: ProjectTreeNodeT;
  canSelectItems?: boolean;
}

const ROOT_LAYER_FOLDER_NAMES = [
  "Well Layers",
  "Completion Layers",
  GRID_LAYERS_SECTION,
  // Region folders inside the grid section; header-only, like the section itself.
  ...GRID_LAYER_FOLDERS.map((folder) => folder.title),
  BASE_WELL_LAYER_PARENT,
  POLYGON_BINS_LAYERS,
  "Shapefile Label Layers",
  GEO_MODEL_LAYERS,
  // Header-only group; its Rigs/Frac Crews children carry the actions.
  RIG_FRAC_LAYER_ROOT
];

export function StructuredNodeManager({ node, canSelectItems }: IStructuredNode) {
  const [{ inEditMode }] = useStructuredNodeContext();
  const projectPermissions = useSelectedProjectPermissions();

  // Root Folders
  if (node.type === "rootFolder") {
    if (ROOT_LAYER_FOLDER_NAMES.includes(node.key)) {
      // Top level root node
      return <RootFolder title={node.title} tooltip={node.tooltip} />;
    } else if (node.title.includes("Shapefiles")) {
      // Top level shapefile root node
      return <RootShapefileFolder node={node} />;
    } else {
      // Sub-level root node
      return <LayerNode node={node} />;
    }
  }
  // Base Layers/Shapefiles
  if (node.type === "layer") {
    if (node.layer.type === "projectShapefile") {
      if (inEditMode && projectPermissions.canEditShapefiles) {
        return <EditableShapefileNode node={node} />;
      }

      return <ShapefileNode node={node} />;
    } else if (node.layer.type === "orgShapefile") {
      return <OrganizationShapefileNode node={node} canSelectItems={canSelectItems} />;
    } else if (node.layer.type === "mcdanShapefile") {
      return <McDanielShapefileNode node={node} canSelectItems={canSelectItems} />;
    } else if (node.layer.type === "polygonBin") {
      return <PolygonBinNode node={node} />;
    } else if (node.key === RIG_LAYER || node.key === FRAC_LAYER) {
      return <RigFracNode node={node} />;
    }

    return <LayerNode node={node} />;
  }

  // Shapefile Folders
  if (node.type === "folder") {
    if (inEditMode && projectPermissions.canEditShapefiles) {
      return (
        //folder?
        <EditableShapefileFolderNode node={node} />
      );
    } else if (
      node.title === POLYGON_BINS_MY_FIELDS_LAYERS ||
      node.title === POLYGON_BINS_ORG_FIELDS_LAYERS ||
      node.title === POLYGON_BINS_PROJECT_FIELDS_LAYERS ||
      node?.layer?.type === "polygonBin"
    ) {
      return <PolygonBinFolder node={node} />;
    }
    return <ShapefileFolderNode node={node} canSelectItems={canSelectItems} />;
  }
}
